import { useRef, useState } from 'react';
import { MdPrint } from 'react-icons/md';
import { post } from '../../Auths/AuthToken';
import setUiMessage from '../../../Utilities/Tostify';
import { DEFAULT_API_URL } from '../../../constents';

const isBrowserPrintableReferralDoc = (url) => {
  const clean = (url || '').split('?')[0].toLowerCase();
  return /\.(pdf|png|jpe?g|gif|webp|txt|html?)$/.test(clean);
};

const resolveReferralDocPrintUrl = (doc) => {
  if (!doc) return '';
  if (doc.docsUrl) {
    if (/^https?:\/\//i.test(doc.docsUrl)) return doc.docsUrl;
    if (doc.docsUrl.startsWith('/')) return `${DEFAULT_API_URL}${doc.docsUrl}`;
    return `${DEFAULT_API_URL}/${doc.docsUrl}`;
  }
  const fileName = doc.docsName || doc.attachment || '';
  if (!fileName) return '';
  if (doc.docsSection === 'results') {
    return `${DEFAULT_API_URL}/uploads/labresults/${fileName}`;
  }
  if (doc.docsSection === 'documentss') {
    return `${DEFAULT_API_URL}/uploads/patientDocuments/${doc.attachment || fileName}`;
  }
  return `${DEFAULT_API_URL}/temp_attachments/${fileName}`;
};

const openReferralPrintWindow = (url = 'about:blank') => {
  return window.open(
    url,
    '',
    'height=800,width=750,resizable=1,fullscreen=0,titlebar=0,menubar=0,toolbar=0,status=0,scrollbars=1',
  );
};

/**
 * Nested Print Referral popup — lists attached docs and prints each individually.
 * First successful print saves/updates referral with sendFax: 'print'.
 */
function PrintReferralPopup({
  documents = [],
  buildReferralPayload,
  initialContext = { refId: 0, rptSentId: 0, reportId: 0 },
  onValidate,
  onClose,
  onSavedClose,
  onReferralCreated,
}) {
  const referralPrintSavingRef = useRef(false);
  const printingDocIndexRef = useRef(null);
  const [referralPrintSaved, setReferralPrintSaved] = useState(false);
  const [referralPrintContext, setReferralPrintContext] = useState(initialContext);

  const handlePrintReferralFile = async (url, existingWindow = null) => {
    if (!url) {
      setUiMessage('err', 'Document file not found.');
      return false;
    }

    if (!isBrowserPrintableReferralDoc(url)) {
      window.open(url, '_blank');
      return true;
    }

    let printWindow =
      existingWindow && !existingWindow.closed ? existingWindow : null;
    if (!printWindow) {
      printWindow = openReferralPrintWindow('about:blank');
    }
    if (!printWindow) {
      setUiMessage(
        'err',
        'Popup blocked! Please allow popups for this website.',
      );
      return false;
    }

    let objectUrl = '';
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
      }
    } catch (_) {
      /* CORS or network — fall through */
    }

    const printSrc = objectUrl || url;

    return new Promise((resolve) => {
      let finished = false;
      const finish = (ok) => {
        if (finished) return;
        finished = true;
        if (objectUrl) {
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        }
        resolve(ok);
      };

      const doPrintOnce = () => {
        if (finished) return;
        if (!printWindow || printWindow.closed) {
          finish(false);
          return;
        }
        try {
          printWindow.focus();
          printWindow.print();
          finish(true);
        } catch (_) {
          finish(false);
        }
      };

      try {
        if (objectUrl) {
          printWindow.location.href = objectUrl;
          printWindow.onload = () => setTimeout(doPrintOnce, 300);
          setTimeout(doPrintOnce, 1500);
          return;
        }

        const safeUrl = String(printSrc)
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/</g, '&lt;');
        printWindow.document.open();
        printWindow.document.write(`<!DOCTYPE html><html><head><title>Print</title>
<style>
  html,body{margin:0;padding:0;height:100%;overflow:hidden;}
  iframe{border:0;width:100%;height:100%;}
  @page{margin:0;}
  @media print{
    html,body{height:auto;overflow:visible;}
    iframe{position:static;width:100%;min-height:100vh;height:100vh;}
  }
</style>
</head><body>
<iframe id="printFrame" src="${safeUrl}"></iframe>
<script>
(function(){
  var done=false;
  function doPrint(){
    if(done) return;
    done=true;
    try{ window.focus(); window.print(); }catch(e){}
    try{ if(window.opener) window.opener.postMessage({type:'referralDocPrintDone'}, '*'); }catch(e){}
  }
  var f=document.getElementById('printFrame');
  if(f){ f.onload=function(){ setTimeout(doPrint, 400); }; }
  setTimeout(doPrint, 2000);
})();
</script>
</body></html>`);
        printWindow.document.close();

        const onMsg = (event) => {
          if (event.data?.type === 'referralDocPrintDone') {
            window.removeEventListener('message', onMsg);
            finish(true);
          }
        };
        window.addEventListener('message', onMsg);
        setTimeout(() => {
          window.removeEventListener('message', onMsg);
          finish(true);
        }, 2500);
      } catch (_) {
        setUiMessage('err', 'Unable to open document for print.');
        if (printWindow && !printWindow.closed) printWindow.close();
        finish(false);
      }
    });
  };

  const saveReferralOnFirstPrint = async () => {
    if (referralPrintSaved) return true;
    if (referralPrintSavingRef.current) return false;

    if (typeof onValidate === 'function' && !onValidate()) {
      return false;
    }

    referralPrintSavingRef.current = true;
    const isUpdate =
      referralPrintContext.refId &&
      referralPrintContext.refId !== '0' &&
      Number(referralPrintContext.refId) !== 0;

    try {
      const payload = {
        formData: {
          ...buildReferralPayload(),
          ...(isUpdate
            ? {
                refId: referralPrintContext.refId,
                rptSentId: referralPrintContext.rptSentId,
                reportId: referralPrintContext.reportId,
              }
            : {}),
        },
        sendFax: 'print',
      };

      const endpoint = isUpdate
        ? 'api/patient/referral/updateReferral'
        : 'api/patient/referral/saveReferral';
      const response = await post(endpoint, payload);

      if (response.status === 200 && response.data?.success) {
        setReferralPrintSaved(true);
        if (!isUpdate && response.data?.referralId) {
          const newId = response.data.referralId;
          setReferralPrintContext((prev) => ({
            ...prev,
            refId: newId,
          }));
          if (typeof onReferralCreated === 'function') {
            onReferralCreated(newId);
          }
        }
        setUiMessage(
          'suc',
          response.data?.message || 'Referral saved and printed successfully',
        );
        return true;
      }
      setUiMessage('err', response.data?.message || 'Failed to save referral');
      return false;
    } catch (err) {
      setUiMessage('err', err?.response?.data?.message || err.message);
      return false;
    } finally {
      referralPrintSavingRef.current = false;
    }
  };

  const closePrintReferralPopup = () => {
    if (referralPrintSaved) {
      if (typeof onSavedClose === 'function') onSavedClose();
      else if (typeof onClose === 'function') onClose();
      return;
    }
    if (typeof onClose === 'function') onClose();
  };

  const printReferralDocument = async (index) => {
    if (printingDocIndexRef.current === index) return;
    const doc = documents[index];
    const url = resolveReferralDocPrintUrl(doc);
    if (!url) {
      setUiMessage('err', 'Document file not found.');
      return;
    }

    printingDocIndexRef.current = index;

    let printWindow = null;
    if (isBrowserPrintableReferralDoc(url)) {
      printWindow = openReferralPrintWindow('about:blank');
    }

    try {
      const ok = await saveReferralOnFirstPrint();
      if (!ok) {
        if (printWindow && !printWindow.closed) printWindow.close();
        printingDocIndexRef.current = null;
        return;
      }
      const printedOk = await handlePrintReferralFile(url, printWindow);
      if (!printedOk && printWindow && !printWindow.closed) {
        printWindow.close();
      }
      printingDocIndexRef.current = null;
    } catch (_) {
      if (printWindow && !printWindow.closed) printWindow.close();
      printingDocIndexRef.current = null;
    }
  };

  return (
    <div className="popup active" id="printReferralPopup" style={{ zIndex: 10001 }}>
      <div
        className="popup__content p-6 overflow-hidden xs bg-white"
        style={{ top: '12%' }}
      >
        <div className="popup-header mb-4">
          <div
            className="fw-600 text-foreground flex items-center gap-2"
            style={{ fontSize: '17px' }}
          >
            <MdPrint size={20} className="text-green-600" />
            Print Referral
          </div>
          <span
            className="close-btn cursor-pointer"
            onClick={closePrintReferralPopup}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') closePrintReferralPopup();
            }}
          >
            &times;
          </span>
        </div>
        <div className="popup-body py-2 space-y-3">
          <div className="mb-5">
            <h4 className="fs-xs text-gray-600 fw-400">
              Click the print icon next to each document to print it individually
            </h4>
          </div>
          <div
            id="printReferralDocsList"
            className="space-y-3 scrollbar-sm"
            style={{ maxHeight: 320, overflowY: 'auto' }}
          >
            {!documents?.length ? (
              <div className="fs-xs text-gray-600 p-2">
                No documents attached. Attach documents to print.
              </div>
            ) : (
              documents.map((doc, index) => {
                const title =
                  doc.docsTitle || doc.docsName || `Document ${index + 1}`;
                return (
                  <div
                    key={`print_doc_${index}_${doc.sectionId || doc.docsName}`}
                    className="flex items-center justify-between p-2 rounded"
                    id={`printReferralDocRow_${index}`}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <img
                        src="./images/file-alt.svg"
                        width={15}
                        className="opacity-50 shrink-0"
                        alt=""
                      />
                      <span className="fs-xs text-gray-700 truncate">{title}</span>
                    </div>
                    <button
                      type="button"
                      className="ml-2 p-1-5 rounded text-green-600 shrink-0 print-referral-doc-btn"
                      onClick={() => printReferralDocument(index)}
                      aria-label={`Print ${title}`}
                    >
                      <MdPrint size={18} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="popup-footer flex justify-end items-center pt-2">
          <button
            type="button"
            className="btn-outline-gray-200 text-slate-600 rounded-md py-2"
            onClick={closePrintReferralPopup}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrintReferralPopup;
